import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from 'react-bootstrap'
import { InputField } from 'reusableComponents/InputField'
import styled from 'styled-components'
import { breakpoints, colors } from 'globalStyles'
import { selectUserEmail, selectMaskedEmail } from 'store/shipping/shippingSelectors'
import { profileUpdateScreen } from 'store/global/globalSelectors'
import { selectUpdatingEmail } from 'store/recipient/recipientSelectors'
import { updateLoginEmail } from 'store/recipient/recipientSlice'

import GuestButton from 'components/GuestButton'
import { sendRecipientLoginEmail } from 'api/shipping'
import { maskEmail } from 'utils/helperFunctions'

const StyledContainer = styled.div`
  .body {
    padding-left: 10%;
    padding-right: 10%;
    padding-top: 10%;
    padding-bottom: 5%;
    height: 100%;
  }

  @media screen and ${breakpoints.sm_above} {
    .body {
      padding-left: 19%;
      padding-right: 19%;
      padding-top: 10%;
      padding-bottom: 5%;
      height: 100%;
    }
  }

  @media screen and ${breakpoints.lg_above} {
    .body {
      padding-left: 28%;
      padding-right: 28%;
      padding-top: 10%;
      padding-bottom: 5%;
      height: 100%;
    }
  }
`
const StyledTitle = styled.span`
  width: 100%;
  text-align: center;
  display: block;
  font-size: 28px;
  line-height: 38px;
  color: ${colors.primary_blue};
  @media screen and ${breakpoints.sm_above} {
    display: block;
    font-size: 32px;
    line-height: 45px;
    margin-bottom: 20px;
  }
`

const StyledSubtitle = styled.span`
  width: 100%;
  display: block;
  text-align: center;
  font-size: 16px;
  line-height: 24px;
`

const StyledLoginFormContent = styled.div`
  text-align: center;
  margin-top: 84px;
  margin-bottom: 84px;

  @media screen and ${breakpoints.sm_above} {
    margin-top: 80px;
    margin-bottom: 60px;
  }

  @media screen and ${breakpoints.lg_above} {
    margin-top: 76px;
    margin-bottom: 60px;
  }

  .form-group {
    margin-top: 24px;
  }

  input[type='text'] {
    border-radius: 4px;
    border: 1px solid ${colors.secondary_grey};
    width: 100%;
  }

  .form-group input:focus:not(.is-invalid) {
    border-radius: 4px;
    border: 2px solid ${colors.text_link_hover};
    outline: none;
  }

  .form-group input.is-invalid,
  .form-group input.is-invalid:focus {
    border-radius: 4px;
    border: 1px solid ${colors.primary_red};
    outline: none;
    box-shadow: none;
  }

  .btn-primary {
    width: 100%;
  }
  .invalidFieldHeader {
    color: ${colors.primary_red} !important;
    text-align: center;
    line-height: 24px;
    margin-bottom: 24px;
    margin-top: 24px;
  }

  .subTitle {
    text-align: center;
    line-height: 28px;
    margin-bottom: 25px;
    margin-top: 25px;
    font-size: 20px;
    font-wight: 600;
    font-style: bold;
  }

  .verifyEmailContent {
    text-align: center;
    margin-bottom: 25px;
    margin-top: 25px;
  }

  .resendText {
    text-align: center;
    line-height: 20px;
    margin-bottom: 25px;
    margin-top: 25px;
    font-size: 14px;
  }

  .noEmailText {
    text-align: center;
    line-height: 20px;
    margin-bottom: 25px;
    margin-top: 25px;
    font-size: 14px;
  }

  .emailStyle {
    color: ${colors.primary_blue};
  }
`

const StyledLink = styled.div`
  margin-top: 16px;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
`

const StyledGuestLink = styled.div`
  margin-top: 24px;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
`

const StyledDiv = styled.div`
  font-size: 14px;
  line-height: 24px;
  text-align: center;
  margin-top: 24px;
`

const StyledButton = styled.div`
  margin-top: 24px;
`

const RegisterLink = styled.a.attrs({
  id: 'register-link',
  rel: 'noreferrer',
  href: '#',
})`
  color: ${colors.primary_blue};
`

const ResendLink = styled.a.attrs((props) => ({
  id: 'resend-link',
  rel: 'noreferrer',
  href: props.disabled ? undefined : '#',
}))`
  color: ${colors.primary_blue};
  cursor: pointer;
  text-decoration: underline;

  ${({ disabled }) =>
    disabled &&
    `
      pointer-events: none;
      color: gray;
      cursor: not-allowed;
      text-decoration: none;
  `}
`

const LoginLink = styled.a.attrs({
  id: 'login-link',
  rel: 'noreferrer',
  href: '#',
})`
  color: ${colors.primary_blue};
`

const LoginForm = () => {
  const { t, i18n } = useTranslation()
  const { lang } = useParams()
  const { language } = i18n
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const defaultMaskedEmail = useSelector(selectMaskedEmail)
  const defaultRawEmail = useSelector(selectUserEmail)

  const fromProfileScreen = useSelector(profileUpdateScreen)

  const updatingEmail = useSelector(selectUpdatingEmail)

  const [emailAddress, setEmailAddress] = useState(defaultMaskedEmail)
  const [verificationLinkSentStatus, setVerificationLinkSentStatus] = useState(false)
  const [resendDisabled, setResendDisabled] = useState(false)
  const [prevLang, setPrevLang] = useState(i18n.language)

  // 0: empty, 1: invalid, 2: valid
  const [emailValidationStatus, setEmailValidationStatus] = useState(0)

  const {
    handleSubmit,
    trigger,
    formState: { isSubmitted },
  } = useForm()

  useEffect(() => {
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang)
    }

    if (prevLang !== language) {
      if (isSubmitted) {
        trigger()
      }
      setPrevLang(language)
    }
  }, [lang, i18n, isSubmitted, trigger, language, prevLang])

  const emailChangeHandler = (event) => {
    const email = event.target.value
    setEmailAddress(email)
  }

  const validateEmail = (emailAddr) => {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    return re.test(String(emailAddr).toLowerCase())
  }

  // Update the method - submitForm
  const submitForm = async () => {
    const rawEmail = defaultRawEmail || emailAddress
    if (validateEmail(rawEmail)) {
      sendRecipientLoginEmail(rawEmail, language)
      setEmailValidationStatus(2)
      setVerificationLinkSentStatus(true)
      dispatch(updateLoginEmail(false))
    } else {
      setEmailValidationStatus(1)
    }
  }

  // Update the method - onClickForgotEmail
  const onClickForgotEmail = (e) => {
    e.preventDefault()
    navigate(`/rs/tr/${language}/forgotemail`, {
      replace: true,
    })
  }

  const onClickRegister = (e) => {
    e.preventDefault()
    navigate(`/rs/tr/${language}/register`, {
      replace: true,
    })
  }

  const onClickResend = (e) => {
    e.preventDefault()
    setResendDisabled(true)
    sendRecipientLoginEmail(emailAddress, language)
    setTimeout(() => {
      setResendDisabled(false)
    }, 30000)
  }

  const onClickLogin = (e) => {
    e.preventDefault()
    setVerificationLinkSentStatus(false)
    setEmailAddress(defaultMaskedEmail)
    setEmailValidationStatus(0)
  }

  return (
    <StyledContainer>
      <div className="body">
        <StyledLoginFormContent>
          <StyledTitle>{fromProfileScreen ? t('login.title_new') : t('login.title')}</StyledTitle>
          {updatingEmail && <StyledSubtitle>{t('login.email_reminder')}</StyledSubtitle>}
          {verificationLinkSentStatus ? (
            <>
              <p className="subTitle">{t('login.verify_email')}</p>
              <p className="verifyEmailContent">
                <Trans
                  t={t}
                  i18nKey="login.verify_email_msg"
                  values={{ emailAddr: maskEmail(emailAddress) }}
                />
              </p>
              <p className="resendText">
                {/* Add action on click of resend */}
                <Trans
                  t={t}
                  i18nKey="login.no_email_received"
                  components={[
                    <ResendLink
                      key="resend-link"
                      onClick={onClickResend}
                      disabled={!!resendDisabled}
                    />,
                  ]}
                />
              </p>
              <p className="noEmailText">
                {/* Add action on click of resend */}
                <Trans
                  t={t}
                  i18nKey="login.incorrect_email"
                  components={[
                    <LoginLink key="login-link" onClick={onClickLogin} />,
                    <RegisterLink key="register-link" onClick={onClickRegister} />,
                  ]}
                />
              </p>
            </>
          ) : (
            <form onSubmit={handleSubmit(submitForm)}>
              <div className="form-group">
                <InputField
                  type="text"
                  id="emailAddr"
                  label={t('login.email_address')}
                  value={emailAddress}
                  onChange={emailChangeHandler}
                  isRequired
                  isInvalid={emailValidationStatus === 1}
                  isValid={emailValidationStatus === 2}
                  disabled={!!defaultMaskedEmail}
                  errorText={t('login.email_addr_invalid_error')}
                />
              </div>
              <StyledLink>
                <Button variant="link" onClick={onClickForgotEmail}>
                  {t('login.forgotEmail')}
                </Button>
              </StyledLink>
              <StyledButton>
                <Button type="submit" variant="primary">
                  {t('login.login_button')}
                </Button>
              </StyledButton>
              <StyledDiv>
                <Trans
                  t={t}
                  i18nKey="login.no_account"
                  components={[<RegisterLink key="register-link" onClick={onClickRegister} />]}
                />
              </StyledDiv>
            </form>
          )}
          <StyledGuestLink>
            <GuestButton isSrcLandingPage={false} />
          </StyledGuestLink>
        </StyledLoginFormContent>
      </div>
    </StyledContainer>
  )
}

export default LoginForm
